import clsx from "clsx"
import {
	LastQueryType,
	PageName,
} from "controllers/dashboard"
import { useTranslation } from "next-i18next"
import { useState } from "react"
import { UseQueryResult } from "react-query"
import { ActiveJob } from "sections/dashboard/active-job"
import { UpgradeButton } from "."
import {
	LockIcon,
	LockIconContainer,
} from "../advanced-profile"
import { CustomThemedResource } from "../image"
import { AvatarWithMenu } from "./avatar-with-menu"

interface DashboardHeaderProps {
	setSubscriptionPopupOpen: (v: boolean) => void
	changeSelectedPage: (name: PageName) => Promise<void>
	setCloseSidebar: (v: boolean) => void
	isClosed: boolean
	activePage: PageName
	lastQuery: LastQueryType
}

export function DashboardHeader(
	props: DashboardHeaderProps,
) {
	const {
		activePage,
		isClosed,
		lastQuery,
		setSubscriptionPopupOpen,
		setCloseSidebar,
		changeSelectedPage,
	} = props
	const { t } = useTranslation()

	return (
		<>
			<div
				className={clsx(
					isClosed
						? "desktop:left-[0px] desktop:w-screen"
						: "desktop:left-[240px] desktop:w-[calc(100%-240px)]",
					"fixed z-[99] bg-color-dashboard-bg py-[6px] pl-2 pr-4 tablet:px-5 desktop:pl-6 desktop:pr-[30px]",
					"flex h-[58px] items-center justify-between border-b tablet:h-[70px]",
					"w-full border-color-separator transition-all duration-[170ms] ease-in-out",
				)}>
				<div className="relative flex flex-row items-center gap-[10px]">
					<button
						onClick={(e) => {
							e.preventDefault()
							setCloseSidebar(!isClosed)
						}}
						className={clsx(
							"peer rounded-[6px] outline-none transition-all",
							"[--icon-color:var(--color-blue-800)] hover:bg-blue-100",
						)}>
						{MenuIcon()}
					</button>
					<div
						className={clsx(
							"left-[0px]",
							"absolute opacity-0 transition-opacity peer-hover:opacity-100",
							"pointer-events-none top-[44px] z-50 select-none",
						)}>
						<div
							className={clsx(
								"relative h-[31px] w-[124px] rounded-[8px] bg-color-tooltip",
								"flex items-center justify-center gap-[4px]",
							)}>
							<span className="text-[13px] font-400 tracking-[0.4px] text-blue-50">
								{isClosed
									? t("txt_open_sidebar")
									: t("txt_close_sidebar")}
							</span>
							<span className="text-[13px] font-400 text-blue-500">
								{isClosed ? "( ] )" : "( [ )"}
							</span>
							<div
								className={clsx(
									"h-[10px] w-[10px] bg-color-tooltip",
									"absolute left-[11px] z-[-1] rotate-45 rounded-[2px]",
									"top-[-4px]",
								)}
							/>
						</div>
					</div>
					<span
						className={clsx(
							"flex text-[18px] font-700 text-blue-800",
							"leading-[16px] tracking-[0.36px]",
						)}>
						{t(`lbl_${activePage}`)}
					</span>
				</div>

				<div className="flex items-center gap-[15px] desktop:gap-[12px]">
					<ActiveJobs lastQuery={lastQuery} />

					<UpgradeButton
						setSubscriptionPopupOpen={
							setSubscriptionPopupOpen
						}
					/>
					<AvatarWithMenu
						changeSelectedPage={changeSelectedPage}
						setSubscriptionPopupOpen={
							setSubscriptionPopupOpen
						}
					/>
				</div>
			</div>
		</>
	)
}

interface ActiveJobsProps {
	lastQuery: UseQueryResult<
		| {
				id: number
				status:
					| "canceled"
					| "failed"
					| "in-queue"
					| "in-progress"
					| "ready"
				thumbnail: string | null | undefined
				isVideo: boolean
				estimatedSeconds: number
				prompt: string
				tool:
					| "deform"
					| "ai_photo"
					| "ai_restyle"
					| "text_to_image"
					| "text_to_video"
					| "ai_suggest"
				model: number | null
		  }[]
		| null,
		unknown
	>
}

function ActiveJobs(props: ActiveJobsProps) {
	const { lastQuery } = props

	const [isJobsOpen, setJobsOpen] = useState(false)
	const { t } = useTranslation()
	const jobsCount = lastQuery.data
		? lastQuery.data.filter((job) => {
				return (
					job.status === "in-queue" ||
					job.status === "in-progress"
				)
		  }).length
		: 0

	return (
		<div className="relative flex h-[50px] w-[50px] items-center">
			<button
				id="active-jobs-container"
				onClick={() => setJobsOpen((o) => !o)}
				className={clsx(
					"h-[36px] w-[36px] rounded-[4px] hover:bg-blue-100",
					"absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2",
					"flex items-center justify-center transition-colors",
				)}>
				<CustomThemedResource
					source="/general/ai-job"
					format="png?a"
					className="h-[24px] w-[24px]"
				/>
			</button>

			<div
				className={clsx(
					jobsCount > 0 ? "flex" : "hidden",
					"absolute right-1 top-1 z-30",
					"h-[18px] w-[18px] rounded-full bg-[#FF3166]",
					"flex items-center justify-center border border-color-surface-cell",
				)}>
				<span className="text-[12px] font-600 text-[#FAF9FB]">
					{jobsCount}
				</span>
			</div>

			<div
				onClick={(e) => {
					if (e.currentTarget) {
						if (
							e.currentTarget.id !== "active-jobs-container"
						) {
							setJobsOpen(false)
						}
					}
				}}
				className={clsx(
					!isJobsOpen && "hidden",
					"fixed left-0 top-0 z-10 h-full w-full",
				)}
			/>
			<div
				onClick={(e) => {
					if (
						e.currentTarget.id !== "active-jobs-container"
					) {
						setJobsOpen(false)
					}
				}}
				className={clsx(
					isJobsOpen ? "flex" : "hidden",
					"fixed z-10 h-screen w-screen bg-color-popup tablet:hidden",
					"left-0 top-[58px]",
				)}
			/>
			<div
				id="active-jobs-container"
				className={clsx(
					isJobsOpen ? "flex" : "hidden",
					"left-0 top-0 z-20 w-screen translate-y-[48px] tablet:w-auto",
					"fixed shrink-0 tablet:drop-shadow-[0px_0px_30px_rgba(0,0,0,0.13)]",
					"tablet:left-auto tablet:right-[10px] tablet:top-[10px]",
					"active-jobs-container no-scrollbar tablet:overflow-y-scroll",
				)}>
				<div
					className={clsx(
						"bg-color-popup-cell tablet:w-[380px] tablet:rounded-[10px]",
						"flex w-full flex-col gap-[16px] px-[8px]",
						"no-scrollbar max-h-[420px] overflow-y-scroll pt-[22px]",
						"pb-[10px] tablet:max-h-[320px]",
						jobsCount === 0 && "h-[320px] tablet:h-[320px]",
					)}>
					<span
						className={clsx(
							"text-[18px] font-600 tablet:tracking-[0.36px]",
							"ml-[8px] text-blue-900",
						)}>
						{t("txt_active_jobs")}
					</span>
					{jobsCount === 0 && (
						<LockIconContainer className="!gap-0 text-[16px] font-500 text-blue-500">
							<LockIcon
								source={"/general/empty-jobs"}
								format="svg"
								className="h-[74px] w-[74px]"
							/>
							{t("txt_no_active_jobs")}
						</LockIconContainer>
					)}
					<div className={clsx("flex w-full flex-col")}>
						{
							// [
							// 	{
							// 		status: "in-queue" as const,
							// 		id: 1234,
							// 		tool: "text_to_image" as const,
							// 		estimatedSeconds: 1212,
							// 		isVideo: false,
							// 		prompt: "kdsj",
							// 	},
							lastQuery.data
								?.filter(
									(j) =>
										j.status === "in-queue" ||
										j.status === "in-progress",
								)
								.map((job, index) => {
									return (
										<ActiveJob
											key={job.id}
											video={job}
											isLast={
												lastQuery.data?.filter(
													(j) =>
														j.status === "in-queue" ||
														j.status === "in-progress",
												).length ===
													1 + index ||
												lastQuery.data?.filter(
													(j) =>
														j.status === "in-queue" ||
														j.status === "in-progress",
												).length === 1
											}
										/>
									)
								})
						}
					</div>
				</div>
			</div>
		</div>
	)
}

const MenuIcon = () => {
	return (
		<svg
			width="32"
			height="32"
			viewBox="0 0 32 32"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				d="M8.60625 22.5H23.3937M8.60625 16H23.3937M8.60625 9.5H23.3937"
				stroke="var(--icon-color)"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}
